import React from 'react';
import { Users, User, Baby, X, Plus, Minus } from 'lucide-react';
import type { Group } from './shoppingCartTypes';

export type ShoppingCartAssistantGroupSectionProps = {
  t: (key: string) => string;
  /** Kept for API compat — section is always expanded in the stepped UI. */
  isGroupDetailsExpanded: boolean;
  setIsGroupDetailsExpanded: (v: boolean) => void;
  groups: Group[];
  addGroup: () => void;
  removeGroup: (groupId: string) => void;
  updateGroup: (groupId: string, field: 'adults' | 'kids', value: number) => void;
  totalAdults: number;
  totalKids: number;
};

function Stepper({
  label,
  icon,
  value,
  onDec,
  onInc,
  decDisabled,
}: {
  label: string;
  icon: React.ReactNode;
  value: number;
  onDec: () => void;
  onInc: () => void;
  decDisabled?: boolean;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-[#3d2817]">{label}</label>
      <div className="flex items-center overflow-hidden rounded-xl border border-[#3d2817]/15 bg-white/90">
        <button
          type="button"
          onClick={onDec}
          disabled={decDisabled}
          className="flex items-center justify-center bg-[#f5f1eb] px-3 py-2 transition-colors hover:bg-[#e8ddd7] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Minus className="h-3.5 w-3.5 text-[#3d2817]" />
        </button>
        <div className="flex flex-1 items-center justify-center gap-1.5 py-2">
          {icon}
          <span className="text-base font-bold text-[#3d2817]">{value}</span>
        </div>
        <button
          type="button"
          onClick={onInc}
          className="flex items-center justify-center bg-[#f5f1eb] px-3 py-2 transition-colors hover:bg-[#e8ddd7]"
        >
          <Plus className="h-3.5 w-3.5 text-[#3d2817]" />
        </button>
      </div>
    </div>
  );
}

export function ShoppingCartAssistantGroupSection({
  t,
  groups,
  addGroup,
  removeGroup,
  updateGroup,
  totalAdults,
  totalKids,
}: ShoppingCartAssistantGroupSectionProps) {
  return (
    <div className="space-y-3">
      {groups.map((group, index) => (
        <div
          key={group.id}
          className="rounded-2xl border border-[#3d2817]/12 bg-white/90 p-3 shadow-sm"
        >
          <div className="mb-3 flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-[#3d2817]">
              <Users className="h-4 w-4 text-[#2f9e44]" aria-hidden />
              {t('shoppingCart.group')} {index + 1}
            </h4>
            {groups.length > 1 && (
              <button
                type="button"
                onClick={() => removeGroup(group.id)}
                className="rounded-full p-1 text-red-600 transition-colors hover:bg-red-50"
                title={t('common.remove')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Stepper
              label={t('shoppingCart.numberOfAdults')}
              icon={<User className="h-4 w-4 text-[#5d4437]" />}
              value={group.adults}
              onDec={() => updateGroup(group.id, 'adults', group.adults - 1)}
              onInc={() => updateGroup(group.id, 'adults', group.adults + 1)}
              decDisabled={group.adults <= 1}
            />
            <Stepper
              label={t('shoppingCart.numberOfKids')}
              icon={<Baby className="h-4 w-4 text-[#5d4437]" />}
              value={group.kids}
              onDec={() => updateGroup(group.id, 'kids', group.kids - 1)}
              onInc={() => updateGroup(group.id, 'kids', group.kids + 1)}
              decDisabled={group.kids <= 0}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addGroup}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#c9a77b]/70 bg-white/50 px-3 py-2.5 text-sm font-medium text-[#3d2817] transition-colors hover:border-[#2f9e44] hover:bg-[#2f9e44]/08 hover:text-[#2f9e44]"
      >
        <Plus className="h-4 w-4" />
        <span>{t('shoppingCart.addGroup')}</span>
      </button>

      <div className="rounded-xl border border-[#2f9e44]/25 bg-[#2f9e44]/08 px-3 py-2.5">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-xs font-medium text-[#3d2817]">{t('shoppingCart.totalGuests')}</span>
          <span className="text-sm font-bold text-[#2f9e44]">
            {totalAdults + totalKids} {t('shoppingCart.guests')}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs text-[#5d4437]">
          <span>
            {t('shoppingCart.adults')}: <strong className="text-[#3d2817]">{totalAdults}</strong>
          </span>
          <span>
            {t('shoppingCart.kids')}: <strong className="text-[#3d2817]">{totalKids}</strong>
          </span>
          <span>
            {t('shoppingCart.totalGroups')}:{' '}
            <strong className="text-[#3d2817]">{groups.length}</strong>
          </span>
        </div>
      </div>
    </div>
  );
}
